// HeroSection — T-011. Full-bleed hero, video with poster fallback, dark
// bottom-up gradient, Anton headline, one lime CTA, optional "for coaches"
// link. The video is skipped on viewports under 768px (a 1.1 MB decorative
// loop is not worth the mobile data) and when prefers-reduced-motion is on.

import { useEffect, useState } from "react";
import type { JSX } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useMediaQuery } from "../../hooks/useMediaQuery";

export function HeroSection(): JSX.Element {
  const { t } = useI18n();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    if (reduceMotion) return;
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Only track while hero is in viewport (< 800px)
          if (window.scrollY <= 850) {
            setScrollY(window.scrollY);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [reduceMotion]);

  const heroStill = isMobile ? "/img/hero-flag-portrait.webp" : "/img/hero-flag.webp";
  const parallaxOffset = reduceMotion ? 0 : Math.min(scrollY * 0.3, 180);

  return (
    <section
      aria-label="hero"
      className="theme-dark-scope relative w-full overflow-hidden border-b border-[var(--color-line)]"
      style={{ minHeight: "min(82vh, 720px)" }}
    >
      {/* Background still image with entry zoom & scroll parallax (T-081 F-5) */}
      <img
        className="hero-parallax absolute inset-0 h-[118%] w-full object-cover will-change-transform"
        src={heroStill}
        alt=""
        width={1376}
        height={768}
        aria-hidden="true"
        style={{
          transform: reduceMotion
            ? "none"
            : `translate3d(0, ${parallaxOffset}px, 0)`,
          transition: "transform 60ms linear",
        }}
      />

      {/* Bottom-up & lateral dark gradient — safeguards 100% legibility across all video push-in frames */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(0deg, rgba(10,11,10,0.96) 0%, rgba(10,11,10,0.75) 45%, rgba(10,11,10,0.25) 75%, transparent 100%), linear-gradient(90deg, rgba(10,11,10,0.85) 0%, rgba(10,11,10,0.45) 40%, transparent 75%)",
        }}
      />

      {/* 1px line grid behind hero, per the design system §4 — 3% opacity, masked away from bright sky */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(var(--color-line) 1px, transparent 1px), linear-gradient(90deg, var(--color-line) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          opacity: 0.03,
          maskImage: "linear-gradient(to bottom, transparent 0%, transparent 30%, black 70%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, transparent 30%, black 70%)",
        }}
      />

      {/* Concrete grain texture, 4% opacity, per the design system §4 */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "url(/img/texture-concrete.webp)",
          backgroundRepeat: "repeat",
          opacity: 0.04,
        }}
      />

      {/* Content */}
      <div className="relative z-10 mx-auto flex h-full max-w-[1280px] flex-col justify-end px-6 pb-16 pt-24 sm:px-10 md:pb-20 md:pt-32">
        <p
          className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-lime)]"
          style={{ textShadow: "0 1px 4px rgba(0,0,0,0.9)" }}
        >
          {t("brand.name")}
        </p>
        <h1
          className="font-display mt-3 text-[clamp(2.5rem,7vw,4.5rem)] uppercase leading-[1.05] tracking-[0.02em] text-[var(--color-ink-hi)]"
          style={{
            maxWidth: "18ch",
            textShadow: "0 2px 16px rgba(0,0,0,0.95), 0 1px 4px rgba(0,0,0,0.8)",
          }}
        >
          {t("landing.hero.headline")}
        </h1>
        <p
          className="mt-4 max-w-prose font-sans text-base text-[var(--color-ink)] sm:text-lg"
          style={{
            lineHeight: 1.5,
            textShadow: "0 1px 8px rgba(0,0,0,0.9)",
          }}
        >
          {t("landing.hero.subcopy")}
        </p>
        <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
          <a
            href="#big6"
            className="inline-flex items-center justify-center bg-[var(--color-lime)] px-6 py-3 font-subhead text-base uppercase tracking-[0.06em] text-[var(--color-void)] transition-colors duration-[120ms] ease-[cubic-bezier(0.2,0,0,1)] hover:bg-[var(--color-lime-hi)] active:bg-[var(--color-lime-lo)]"
            style={{ minHeight: "44px", borderRadius: "var(--radius-sharp)" }}
          >
            {t("landing.hero.cta")}
          </a>
          <Link
            to="/app/coach"
            className="font-sans text-sm text-[var(--color-ink-mute)] underline-offset-4 transition-colors duration-[120ms] hover:text-[var(--color-ink-hi)] hover:underline"
          >
            {t("landing.hero.coachLink")} →
          </Link>
        </div>
      </div>
    </section>
  );
}
